import React, { useState } from 'react';
import { resolveAccountingDocumentLinks } from '../../services/deal-accounting-links';
import { Button } from '../../../../shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../../shared/ui/dialog';
import IconActionButton from '../../../../shared/components/patterns/IconActionButton';
import {
  downloadDocumentFileAuthenticated,
  getDocumentPreviewObjectUrl,
  listDocumentsResult,
  uploadDocument,
} from '@/modules/documents/services';

const ACCOUNTING_LINKED_MODULE = 'ACCOUNTING';

function getDocumentErrorMessage(result) {
  if (result?.error === 'DOCUMENTS_MODULE_DISABLED') {
    return 'Modulo documenti non abilitato per questo workspace.';
  }

  if (
    result?.error === 'DOCUMENTS_AUTH_REQUIRED' ||
    result?.error === 'DOCUMENTS_SESSION_EXPIRED' ||
    result?.error === 'DOCUMENTS_SESSION_REFRESH_REQUIRED'
  ) {
    return result.message || 'Sessione non autorizzata per il backend documenti.';
  }

  if (result?.error?.startsWith?.('DOCUMENTS_TENANT_DATABASE_')) {
    return result.message || 'Database tenant documenti non disponibile.';
  }

  return result?.message || 'Documenti non disponibili.';
}

export default function AccountingAttachmentsPanel({
  entity,
  linkedEntityType,
  entityLabel,
  clientName = '',
  onUpload,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [message, setMessage] = useState('');
  const entityId = entity?.id;
  const linkedModule = ['IN_PROGRESS_DEAL', 'COMPLETED_DEAL', 'PROPOSAL'].includes(linkedEntityType) ? 'DEALS' : ACCOUNTING_LINKED_MODULE;

  async function loadAttachments() {
    if (!entityId) return;

    setIsLoading(true);
    setMessage('');
    let links = [{ linkedModule, linkedEntityType, linkedEntityId: entityId }];
    try {
      links = await resolveAccountingDocumentLinks(entity, linkedEntityType);
    } catch (error) {
      setMessage(error.message);
    }
    const results = await Promise.allSettled(links.map((link) => listDocumentsResult(link)));
    const loaded = [];
    for (const result of results) {
      if (result.status === 'fulfilled' && result.value?.ok) loaded.push(...result.value.data.documents);
      else setMessage(result.status === 'rejected' ? 'Alcuni allegati non sono disponibili. Riprova.' : getDocumentErrorMessage(result.value));
    }
    setDocuments([...new Map(loaded.map((document) => [String(document.id), document])).values()]);
    setIsLoading(false);
  }

  async function openPanel() {
    setIsOpen(true);
    await loadAttachments();
  }

  async function handleUpload(event) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !entityId) return;

    setIsUploading(true);
    setMessage('');
    const result = await uploadDocument({
      file,
      form: {
        uploadedBy: 'Contabilita',
        client: clientName || 'Contabilita',
        clientId: entity?.clientId || '',
        linkedModule,
        linkedEntityType,
        linkedEntityId: entityId,
        category: 'Allegato contabile',
      },
    });
    setIsUploading(false);

    if (!result?.ok) {
      setMessage(getDocumentErrorMessage(result));
      return;
    }

    setDocuments((current) => [
      result.data.document,
      ...current.filter((document) => String(document.id) !== String(result.data.document.id)),
    ]);
    onUpload?.(result.data.document);
  }

  async function handlePreview(document) {
    const result = await getDocumentPreviewObjectUrl(document);
    if (!result.ok) {
      setMessage(getDocumentErrorMessage(result));
      return;
    }

    window.open(result.data.objectUrl, '_blank', 'noopener,noreferrer');
  }

  async function handleDownload(document) {
    const result = await downloadDocumentFileAuthenticated(document);
    if (!result.ok) {
      setMessage(getDocumentErrorMessage(result));
    }
  }

  return (
    <>
      <IconActionButton
        iconKey="actions.attached"
        label="Allegati"
        title="Allegati"
        onClick={openPanel}
      />

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Allegati {entityLabel}</DialogTitle>
            <DialogDescription>Allegati condivisi tra l’affare, la fattura e gli incassi collegati.</DialogDescription>
          </DialogHeader>

            <label className="mb-4 block rounded-xl border border-dashed border-[var(--wt-card-border)] p-4 text-sm text-[var(--wt-text-secondary)]">
              <span className="font-medium">{isUploading ? 'Caricamento...' : 'Carica allegato'}</span>
              <input
                type="file"
                className="mt-2 block w-full text-sm"
                disabled={isUploading}
                onChange={handleUpload}
              />
            </label>

            {message ? (
              <div role="alert" className="mb-4 text-sm wt-inline-error">
                {message}
              </div>
            ) : null}

            {isLoading ? (
              <p className="text-sm text-[var(--wt-text-secondary)]">Caricamento allegati...</p>
            ) : documents.length === 0 ? (
              <p className="rounded-xl border border-[var(--wt-card-border)] bg-[var(--wt-surface-muted)] p-4 text-sm text-[var(--wt-text-secondary)]">
                Nessun allegato collegato.
              </p>
            ) : (
              <div className="space-y-3">
                {documents.map((document) => (
                  <div
                    key={document.id}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[var(--wt-card-border)] px-3 py-2"
                  >
                    <div>
                      <p className="text-sm font-medium text-[var(--wt-text-primary)]">{document.name}</p>
                      <p className="text-xs text-[var(--wt-text-secondary)]">{document.fileName}</p>
                    </div>
                    <div className="flex gap-2">
                      {document.mimeType === 'application/pdf' ? (
                        <Button size="sm" variant="outline" onClick={() => handlePreview(document)}>
                          Preview
                        </Button>
                      ) : null}
                      <Button size="sm" variant="outline" onClick={() => handleDownload(document)}>
                        Download
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
        </DialogContent>
      </Dialog>
    </>
  );
}
